// Localização: frontend/src/components/Financeiro/IndicadoresSaudeFinanceira.jsx
// Componente de Indicadores de Saúde Financeira

import React from 'react';
import Card from '../UI/Card';
import CardContent from '../UI/CardContent';
import { TrendingUp, TrendingDown, Percent, DollarSign, Activity } from 'lucide-react';

/**
 * @component IndicadoresSaudeFinanceira
 * @description Componente que exibe indicadores importantes de saúde financeira
 * @param {Object} resumo - Dados do resumo financeiro
 * @param {boolean} isLoading - Estado de carregamento
 */
const IndicadoresSaudeFinanceira = ({ resumo, isLoading = false }) => {
    if (isLoading || !resumo) {
        return (
            <Card className="h-full flex flex-col">
                <CardContent className="pt-5 pb-5 px-5 h-full flex flex-col">
                    <div className="flex items-center gap-3 mb-5">
                        <Activity className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-lg font-bold text-gray-800">Indicadores de Saúde Financeira</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-lg" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        );
    }

    const receitaTotal = parseFloat(resumo.receita_total || 0);
    const despesaTotal = parseFloat(resumo.despesa_total || 0);
    const receitasPendentes = parseFloat(resumo.receitas_pendentes || 0);
    const despesasPendentes = parseFloat(resumo.despesas_pendentes || 0);
    const saldoAtual = receitaTotal - despesaTotal;

    // Calcular indicadores
    const margemLucro = receitaTotal > 0 
        ? ((saldoAtual / receitaTotal) * 100).toFixed(1)
        : 0;

    const saldoLiquido = saldoAtual + receitasPendentes - despesasPendentes;

    const taxaCobertura = despesaTotal > 0
        ? ((receitaTotal / despesaTotal) * 100).toFixed(1)
        : receitaTotal > 0 ? 100 : 0;

    const indicadores = [
        {
            label: 'Margem de Lucro',
            value: `${margemLucro}%`,
            icon: Percent,
            color: parseFloat(margemLucro) >= 20 ? 'text-green-600' : parseFloat(margemLucro) >= 10 ? 'text-yellow-600' : 'text-red-600',
            bgColor: parseFloat(margemLucro) >= 20 ? 'bg-green-100' : parseFloat(margemLucro) >= 10 ? 'bg-yellow-100' : 'bg-red-100',
            description: 'Lucro líquido sobre receita'
        },
        {
            label: 'Saldo Líquido',
            value: new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(saldoLiquido),
            icon: DollarSign,
            color: saldoLiquido >= 0 ? 'text-green-600' : 'text-red-600',
            bgColor: saldoLiquido >= 0 ? 'bg-green-100' : 'bg-red-100',
            description: 'Saldo + Pendentes'
        },
        {
            label: 'Taxa de Cobertura',
            value: `${taxaCobertura}%`,
            icon: Activity,
            color: parseFloat(taxaCobertura) >= 100 ? 'text-green-600' : 'text-red-600',
            bgColor: parseFloat(taxaCobertura) >= 100 ? 'bg-green-100' : 'bg-red-100',
            description: 'Receita cobre despesas'
        },
        {
            label: 'Saldo Atual',
            value: new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(saldoAtual),
            icon: saldoAtual >= 0 ? TrendingUp : TrendingDown,
            color: saldoAtual >= 0 ? 'text-green-600' : 'text-red-600',
            bgColor: saldoAtual >= 0 ? 'bg-green-100' : 'bg-red-100',
            description: 'Receita - Despesa'
        }
    ];

    return (
            <Card className="transition-all duration-300 hover:shadow-lg h-full flex flex-col">
            <CardContent className="pt-5 pb-5 px-5 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-5">
                    <Activity className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-bold text-gray-800">Indicadores de Saúde Financeira</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {indicadores.map((indicador, index) => {
                        const Icon = indicador.icon;
                        return (
                            <div
                                key={index}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <p className="text-xs font-medium text-gray-600 mb-1">
                                            {indicador.label}
                                        </p>
                                        <p className={`text-lg font-bold ${indicador.color}`}>
                                            {indicador.value}
                                        </p>
                                    </div>
                                    <div className={`p-2 rounded-full ${indicador.bgColor} flex-shrink-0`}>
                                        <Icon className={`h-5 w-5 ${indicador.color}`} />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    {indicador.description}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

export default IndicadoresSaudeFinanceira;

